// @flow
import ResourceFactory from 'app/common/lib/ResourceFactory';
import type { Action } from 'app/common/redux/actions/types';
import type { Dispatch } from 'redux';
import type { Thunk } from 'app/common/redux/types';
import type { Board, User } from 'app/common/react/components/messages/types';

export type ConversationId = string;
type Image = { url: string, width: number, height: number };
export type Message = {|
  board: ?$Shape<Board>,
  created_at: string,
  created_ms: number,
  event_type: null, // legacy of conversation group boards
  id: string,
  sender: {
    username: string,
    first_name: string,
    full_name: string,
    id: string,
    image_large_url: string,
    image_medium_url: string,
    image_small_url: string,
    image_xlarge_url: string,
  },
  text: ?string,
  type: 'message',
  pin: ?{
    dominant_color: string,
    id: string,
    images: {
      '136x136': Image,
      '236x': Image,
      '474x': Image,
      '736x': Image,
      orig: Image,
    },
  },
  user: ?$Shape<User>,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  user_did_it_data: *,
|};

export type Conversation = {
  id: ConversationId,
  board: ?Board,
  emails: Array<string>,
  name: ?string,
  unread: number,
  users: Array<User>,
  last_message: ?Message,
  // TODO(bca): messages
};

export function conversationShow(id: ConversationId): Action {
  return {
    type: 'CONVERSATION_SHOW',
    payload: { id },
  };
}
export type ConversationShow = {
  type: 'CONVERSATION_SHOW',
  payload: { id: ConversationId },
};

export function conversationOpen(id: ConversationId): Action {
  return {
    type: 'CONVERSATION_OPEN',
    payload: { id },
  };
}
export type ConversationOpen = {
  type: 'CONVERSATION_OPEN',
  payload: { id: ConversationId },
};

export function conversationClose(id: ConversationId): Action {
  return {
    type: 'CONVERSATION_CLOSE',
    payload: { id },
  };
}
export type ConversationClose = {
  type: 'CONVERSATION_CLOSE',
  payload: { id: ConversationId },
};

export function conversationCreate(conversation: Conversation): Action {
  return {
    type: 'CONVERSATION_CREATE',
    payload: conversation,
  };
}
export type ConversationCreate = {
  type: 'CONVERSATION_CREATE',
  payload: Conversation,
};

export function conversationDelete(id: ConversationId): Action {
  return {
    type: 'CONVERSATION_DELETE',
    payload: { id },
  };
}
export type ConversationDelete = {
  type: 'CONVERSATION_DELETE',
  payload: { id: ConversationId },
};

export function conversationBadgeReset() {
  return (dispatch: Dispatch<any>) => {
    const resource = ResourceFactory.create('ConversationBadgeResource', {});
    resource.callDelete({ showError: false });
    dispatch({
      type: 'CONVERSATION_BADGE_RESET',
    });
  };
}
export type ConversationBadgeReset = { type: 'CONVERSATION_BADGE_RESET' };

export function pendingMessageSend(id: ConversationId, message: Message): Action {
  return {
    type: 'MESSAGE_SEND_PENDING',
    payload: {
      id,
      message,
    },
  };
}
export type PendingMessageSend = {
  type: 'MESSAGE_SEND_PENDING',
  payload: {
    id: ConversationId,
    message: Message,
  },
};

export function pendingMessageClear(
  id: ConversationId,
  messageId: string,
  newMessageId: string,
): Action {
  return {
    type: 'MESSAGE_SEND_PENDING_CLEAR',
    payload: {
      id,
      messageId,
      newMessageId,
    },
  };
}
export type PendingMessageClear = {
  type: 'MESSAGE_SEND_PENDING_CLEAR',
  payload: {
    id: ConversationId,
    messageId: string,
    newMessageId: string,
  },
};

const uuid = (() => {
  let id = 0;
  // eslint-disable-next-line no-plusplus
  return () => id++;
})();
export function messageSend(
  conversationId: string,
  message: {
    text: ?string,
    pinId?: ?string,
    boardId?: ?string,
    userId?: ?string,
    didItId?: ?string,
  },
  source?: string,
) {
  let action;
  const promise = new Promise((resolve) => {
    // $FlowFixMe Flow v0.104.0 upgrade supression.
    action = (dispatch: Dispatch<any>, getState: *) => {
      const { text, pinId, boardId, userId, didItId } = message;
      const state = getState();

      function sendMessage() {
        const sendMessageResource = ResourceFactory.create('ConversationMessagesResource', {
          conversation_id: conversationId,
          text,
          pin: pinId,
          board: boardId,
          user: userId,
          user_did_it_data: didItId,
          source,
        });
        // get a unique key to use as a fake conversation id
        const key = `${uuid()}`;
        sendMessageResource.callCreate().then((resp: any) => {
          dispatch(pendingMessageClear(conversationId, key, resp.resource_response.data.id));
        });
        dispatch(
          pendingMessageSend(conversationId, {
            text,
            sender: state.viewer,
            created_at: new Date().toString(),
            created_ms: Date.now(),
            id: key,
            board: null,
            event_type: null,
            pin: pinId ? state.pins[pinId] : null,
            type: 'message',
            user_did_it_data: null,
            user: null,
          }),
        );
        resolve();
      }

      if (!state.conversations.data[conversationId]) {
        ResourceFactory.create('ConversationResource', {
          conversation_id: conversationId,
        })
          .callGet()
          .then((response: any) => {
            const conversation =
              response && response.resource_response && response.resource_response.data;
            dispatch(conversationCreate(conversation));
            sendMessage();
          });
      } else {
        sendMessage();
      }
    };

    action.getPromise = () => promise;
  });

  return action;
}

export const conversationGet = (
  user_ids: Array<string>,
  emails: Array<string>,
  onErrors: (error: string) => void,
): Thunk<void> => (dispatch) =>
  ResourceFactory.create('ConversationsResource', {
    user_ids,
    emails,
    text: '',
  })
    .callCreate()
    .then((response: any) => {
      const conversation = response && response.resource_response.data;
      dispatch(conversationOpen(conversation.id));
    })
    .catch((error) => {
      const errMessage = error.message_detail || error.message || '';
      onErrors(errMessage);
    });
